import { Component } from '@theme/component';

/**
 * Quantity breaks (descuentos por volumen) para Horizon.
 *
 * Muestra hasta 4 tramos de cantidad como opciones seleccionables. Al elegir un
 * tramo, escribe la cantidad correspondiente en el input `name="quantity"` del
 * formulario de producto, de modo que el botón nativo "Agregar al carrito" de
 * Horizon agregue la cantidad correcta sin necesidad de modificar product-form.js.
 *
 * El DESCUENTO REAL lo aplica un Descuento automático nativo de Shopify
 * (Admin → Descuentos). Este componente es solo la UI de selección y el cálculo
 * de precios mostrados.
 *
 * @typedef {object} QuantityBreaksRefs
 * @property {HTMLInputElement[]} breakInput - Los radios de cada tramo.
 *
 * @extends {Component<QuantityBreaksRefs>}
 */
class QuantityBreaksComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Sincroniza el estado visual + la cantidad con el tramo marcado al cargar.
    const checked = this.#getCheckedInput();
    if (checked) {
      this.#updateSelectedState(checked);
      this.#syncQuantity(Number(checked.value));
    }
  }

  /**
   * Maneja el cambio de tramo (disparado por on:change en cada radio).
   * @param {Event} event
   */
  handleChange(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    if (!target || target.type !== 'radio') return;

    const quantity = Number(target.value);
    if (!quantity || Number.isNaN(quantity)) return;

    this.#updateSelectedState(target);
    this.#syncQuantity(quantity);
  }

  /** @returns {HTMLInputElement | null} */
  #getCheckedInput() {
    return this.querySelector('input[type="radio"]:checked');
  }

  /**
   * Marca visualmente el tramo activo. No depende solo del selector CSS
   * `:checked +` — agrega una clase explícita al label, de modo que el estado
   * sobreviva a re-renders del editor y a cualquier rareza de navegador.
   * @param {HTMLInputElement} input
   */
  #updateSelectedState(input) {
    const inputs = this.querySelectorAll('input[type="radio"]');
    inputs.forEach((el) => {
      const isActive = el === input;
      el.checked = isActive;
      const label = el.nextElementSibling;
      if (label) label.classList.toggle('is-selected', isActive);
    });
  }

  /**
   * Encuentra el formulario de producto al que pertenece este block.
   *
   * 1) Colocación recomendada: el block vive DENTRO de buy-buttons, así que el
   *    product-form-component es un ancestro y `closest` lo encuentra.
   * 2) Fallback: si el merchant colocó el block como HERMANO de buy-buttons
   *    (p. ej. arriba del botón para mostrar los tramos antes), `closest` falla.
   *    En ese caso buscamos el único product-form-component de la misma sección.
   * @returns {HTMLElement | null}
   */
  #getForm() {
    const inside = this.closest('product-form-component');
    if (inside) return inside;

    const scope = this.closest('.shopify-section') || document;
    return scope.querySelector('product-form-component');
  }

  /**
   * Escribe la cantidad en el input de cantidad del formulario de producto
   * y dispara los eventos para que Horizon recalcule todo (price-per-item, etc.).
   * @param {number} quantity
   */
  #syncQuantity(quantity) {
    const form = this.#getForm();
    if (!form) return;

    // 1) Vía API pública del quantity-selector (si existe) — respeta min/max/step.
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.setValue) {
      quantitySelector.setValue(quantity);
    }

    // 2) Fallback directo al input, por si el selector de cantidad está oculto/ausente.
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      form.querySelector('input[name="quantity"]')
    );
    if (quantityInput && Number(quantityInput.value) !== quantity) {
      quantityInput.value = String(quantity);
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('quantity-breaks-component')) {
  customElements.define('quantity-breaks-component', QuantityBreaksComponent);
}

